import {type ReactNode} from 'react';
import {EditorToolbar} from '@tinct/web-components';
import type {StudioViewMode} from '../hooks/use-studio-view-mode';

/** Bottom-toolbar interaction tools. `chat` is reserved for a later iteration. */
export type StudioTool = 'select' | 'text';

type StudioLayoutProps = {
  /**
   * The persistent conversation column (left, 320px). Omit to give the canvas
   * the full width — e.g. the studio's original tab on an EXISTING-origin
   * campaign, where no chat agent applies.
   */
  chatPanel?: ReactNode;
  /** Canvas area content — rendered inside the relative flex-1 container. */
  children: ReactNode;
  /**
   * "View original content" toggle. Omit to hide it (and its shortcut) —
   * the studio's original tab has no contextualised content to peek behind.
   */
  hideBlocks?: boolean;
  onHideBlocksChange?: (hidden: boolean) => void;
  /**
   * Active interaction tool (select / text). Omit to hide the tools segment
   * of the toolbar — hosts without inline text edition (the hosted-page edit
   * step, the studio's original tab) render only the other segments.
   */
  tool?: StudioTool;
  onToolChange?: (tool: StudioTool) => void;
  /** Single viewport vs the multi-breakpoint canvas. */
  viewMode: StudioViewMode;
  onViewModeChange: (mode: StudioViewMode) => void;
};

/**
 * The shared editor-screen frame: conversation column on the left, canvas on
 * the right, and the editor toolbar centered at the bottom. Used by the
 * studio (preview & refine step) and the hosted-page edit step so both keep
 * the exact same main layout.
 */
export const StudioLayout = ({
  chatPanel,
  children,
  hideBlocks,
  onHideBlocksChange,
  tool,
  onToolChange,
  viewMode,
  onViewModeChange,
}: StudioLayoutProps) => (
  <div className="flex size-full flex-col bg-background">
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Persistent conversation column */}
      {chatPanel != null && (
        <div className="flex h-full w-[320px] shrink-0 flex-col overflow-hidden">
          {chatPanel}
        </div>
      )}

      {/* Canvas */}
      <div className="relative flex min-h-0 min-w-0 flex-1">
        {children}

        {/* Bottom toolbar — a dark pill floating over the canvas card. Scoped
            to the canvas container (not a full-width fixed strip) so it can
            never sit over the chat composer's submit button. */}
        <div className="absolute bottom-5 left-1/2 z-20 -translate-x-1/2">
          <EditorToolbar
            className="dark shadow-lg"
            tool={tool}
            onToolChange={onToolChange}
            originalContent={hideBlocks}
            onOriginalContentChange={onHideBlocksChange}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        </div>
      </div>
    </div>
  </div>
);
